import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormGroup, FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { forkJoin, of } from 'rxjs';
import { catchError, filter, take } from 'rxjs/operators';

import { MakersFinancesMgrService } from '../../services/makers-finances-mgr.service';
import { PoPermissionsConfigService } from '../../services/po-permissions-config.service';
import { StoreAuxService } from '../../../services/storeAux/store-aux.service';
import { AlertsService } from '../../../services/alerts/alerts.service';
import { AlertsType } from '@rappi/ui/alerts';
import {
  CatalogItem,
  FinancesConfig,
  NewPurchaseOrderConfig,
  PurchaseOrderComment,
  PurchaseOrderDetail,
} from '../../models/makers-finances-mgr.models';
import {
  PO_LABEL_TO_CODE, PO_EMPTY_VALUE,
  PO_FIELD, SOURCE_TYPE, DETAIL_MODE,
  PO_DIALOG_TYPE,
  PO_ACTION, PO_STATUS_BANNER,
  PO_QUERY_PARAM, PO_VIEW_TOOLTIP,
  PO_ERROR_ALERT_DURATION_MS, PO_HTTP_STATUS,
  BUSINESS_UNIT, BUSINESS_UNIT_LABEL, BusinessUnit,
  PO_TYPE, BUDGET_TYPE_LABEL, BudgetType,
} from '../purchase-order-detail/purchase-order.constants';
import { PoActionDialogComponent } from '../po-action-dialog/po-action-dialog.component';
import { PoConfirmDialogComponent } from '../purchase-order-detail/po-confirm-dialog/po-confirm-dialog.component';
import { STORAGE_KEY } from '../../../definitions/app.constants';

interface ViewComment {
  id: number;
  author: string;
  initials: string;
  date: string;
  text: string;
}

@Component({
  selector: 'app-purchase-order-view',
  templateUrl: './purchase-order-view.component.html',
  styleUrls: ['./purchase-order-view.component.scss']
})
export class PurchaseOrderViewComponent implements OnInit {
  loading = true;
  po: PurchaseOrderDetail | null = null;
  newComment = '';

  // Exposed for the template so empty fields render a placeholder instead of disappearing.
  readonly EMPTY_VALUE = PO_EMPTY_VALUE;
  readonly SOURCE_TYPE = SOURCE_TYPE;
  readonly VIEW_TOOLTIP = PO_VIEW_TOOLTIP;

  // Skeleton placeholder data — each entry represents one card; fields.length controls the
  // number of shimmer bars shown in the 2-column grid while the real data loads.
  readonly skeletonCards = [
    { fields: Array(8) },  // General information
    { fields: Array(2) },  // PO document
    { fields: Array(6) },  // Budget & Dates
    { fields: Array(4) },  // Buyer information
    { fields: Array(4) },  // Additional Info
    { fields: Array(8) },  // Audit
  ];
  readonly skeletonSidebarCards = [
    { fields: Array(3) },  // Approval actions
    { fields: Array(3) },  // Comments
  ];

  // Resolved names for IDs returned by the detail endpoint
  sourceTypeName = '';
  sourceTypeCode = '';
  poTypeName = '';
  poTypeCode = '';
  statusCode: string | null = null;
  comments: ViewComment[] = [];
  private remoteConfig: NewPurchaseOrderConfig | null = null;
  // Full CMS config for the document section — its limits live at the top level
  // (po_document_max_count / po_document_max_size_mb), siblings of new_purchase_order.
  financesConfig: FinancesConfig | null = null;
  private statusBanners: Partial<Record<string, { message: string }>> = {};
  whitelistApprovers: Record<string, string[]> | null = null;
  userEmail = '';
  // CMS rollout flag (FinancesConfig.po_business_unit_enabled). While false, the Business Unit
  // row is hidden entirely and the view always shows "Maker to execute" (legacy behavior).
  poBusinessUnitEnabled = false;
  // CMS rollout flag (FinancesConfig.budget_type_validation_enabled). While false, the Budget type
  // row is hidden entirely, even for Partnerships POs (legacy behavior).
  budgetTypeValidationEnabled = false;
  // CMS rollout flag (FinancesConfig.trato_expiration_date_enabled). While false, the Expiration
  // Date row is hidden entirely, even for Trato POs with an already-persisted value.
  tratoExpirationDateEnabled = false;

  // Minimal form for app-section-po-document in view mode — carries the uploaded docs array.
  docForm = new FormGroup({
    [PO_FIELD.DOCUMENT_FILE]: new FormControl([]),
  });
  readonly DOC_SOURCE_TYPE = SOURCE_TYPE.NONE;
  readonly DOC_MODE = DETAIL_MODE.EDIT;

  private readonly sourceTypeNameById = new Map<number, string>();
  private readonly poTypeNameById = new Map<number, string>();
  private readonly poTypeCodeById = new Map<number, string>();

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly dialog: MatDialog,
    private readonly financesService: MakersFinancesMgrService,
    private readonly permissionsConfig: PoPermissionsConfigService,
    private readonly storeAux: StoreAuxService,
    private readonly alertsService: AlertsService,
  ) {}

  get canEdit(): boolean {
    return this.permissionsConfig.canPerformAction(this.sourceTypeCode, this.statusCode, PO_ACTION.EDIT);
  }

  get canCancel(): boolean {
    return this.permissionsConfig.canPerformAction(this.sourceTypeCode, this.statusCode, PO_ACTION.CANCEL_PO);
  }

  get canClose(): boolean {
    return this.permissionsConfig.canPerformAction(this.sourceTypeCode, this.statusCode, PO_ACTION.CLOSE_PO);
  }

  get canReopen(): boolean {
    return this.permissionsConfig.canPerformAction(this.sourceTypeCode, this.statusCode, PO_ACTION.REOPEN_PO);
  }

  get canComment(): boolean {
    return !this.permissionsConfig.isCommonActionHidden('comment', this.statusCode);
  }

  get statusBanner(): { bg: string; iconColor: string; message: string } | null {
    if (!this.statusCode) {
      return null;
    }
    const colors = PO_STATUS_BANNER[this.statusCode];
    if (!colors) {
      return null;
    }
    const message = this.statusBanners[this.statusCode]?.message;
    if (!message) {
      return null;
    }
    return { bg: colors.bg, iconColor: colors.iconColor, message };
  }

  ngOnInit(): void {
    const idParam = this.route.snapshot.paramMap.get('id');
    const id = Number(idParam);
    if (!idParam || !Number.isFinite(id)) {
      this.alertsService.openAlerts('Invalid purchase order id.', AlertsType.warning);
      this.goBack();
      return;
    }

    forkJoin({
      po: this.financesService.getPurchaseOrder(id),
      sourceTypes: this.financesService.getSourceTypes(),
      poTypes: this.financesService.getPoTypeOptions(),
    }).pipe(
      catchError(err => {
        this.loading = false;
        const status = err?.status;
        if (status === 401) {
          this.alertsService.openAlerts('You are not authorized to view this purchase order.', AlertsType.warning);
        } else if (status === 404) {
          this.alertsService.openAlerts('Purchase order not found.', AlertsType.warning);
        } else {
          this.alertsService.openAlerts('Error loading purchase order. Please try again.', AlertsType.error);
        }
        this.goBack();
        return of(null);
      }),
    ).subscribe(result => {
      if (!result) {
        return;
      }
      this.loading = false;
      this.po = result.po.data;

      result.sourceTypes.data.records.forEach((r: CatalogItem) => this.sourceTypeNameById.set(r.id, r.name));
      result.poTypes.data.records.forEach((r: CatalogItem) => {
        this.poTypeNameById.set(r.id, r.name);
        this.poTypeCodeById.set(r.id, r.code);
      });

      const sourceTypeId = this.po.source_type_id;
      const sourceRecord = sourceTypeId != null
        ? result.sourceTypes.data.records.find((r: CatalogItem) => r.id === sourceTypeId)
        : null;
      this.sourceTypeCode = sourceRecord?.code ?? '';
      this.sourceTypeName = sourceRecord?.name ?? '';
      this.poTypeName = this.po.po_type_id != null
        ? (this.poTypeNameById.get(this.po.po_type_id) ?? '')
        : '';
      this.poTypeCode = this.po.po_type_id != null
        ? (this.poTypeCodeById.get(this.po.po_type_id) ?? '')
        : '';
      this.statusCode = this.po.stage ? PO_LABEL_TO_CODE[this.po.stage] ?? null : null;
      this.comments = this.po.comments.map(c => this.toViewComment(c));

      if (this.po.documents?.length) {
        this.docForm.get(PO_FIELD.DOCUMENT_FILE)?.setValue(
          this.po.documents.map(doc => ({
            id: doc.id,
            key: doc.key,
            name: doc.file_name,
            uploadedAt: doc.uploaded_at,
          })),
        );
      }
    });

    this.financesService.config$
      .pipe(filter(c => !!c), take(1))
      .subscribe((config: FinancesConfig) => {
        this.remoteConfig = config.new_purchase_order;
        this.financesConfig = config;
        this.statusBanners = config.purchase_order_details?.status_banners ?? {};
        this.whitelistApprovers = config.purchase_order_whitelist_approvers ?? null;
        this.poBusinessUnitEnabled = !!config.po_business_unit_enabled;
        this.budgetTypeValidationEnabled = !!config.budget_type_validation_enabled;
        this.tratoExpirationDateEnabled = !!config.trato_expiration_date_enabled;
      });

    this.userEmail = localStorage.getItem(STORAGE_KEY.email) ?? '';

    this.permissionsConfig.loadConfig().pipe(take(1)).subscribe();
  }

  formatCurrency(amount: number | null, currency: string | null): string {
    if (amount == null) {
      return '';
    }
    const locale = currency === 'BRL' ? 'pt-BR' : currency === 'MXN' ? 'es-MX' : 'es-CO';
    return new Intl.NumberFormat(locale, {
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  }

  formatDate(dateStr: string | null): string {
    if (!dateStr) {
      return '';
    }
    const date = new Date(dateStr);
    if (Number.isNaN(date.getTime())) {
      return '';
    }
    return date.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
  }

  // Dedicated to date-only "YYYY-MM-DD" strings (e.g. expiration_date) — unlike formatDate/
  // formatDateTime, never does `new Date(dateStr)` on the raw string, which parses as UTC midnight
  // and can display the wrong calendar day once read back with local getters in negative-UTC-offset
  // timezones. Parses the components directly and builds a local-midnight Date instead.
  formatDateOnly(dateStr: string | null): string {
    if (!dateStr) {
      return '';
    }
    const [y, m, d] = dateStr.split('-').map(Number);
    if (!y || !m || !d) {
      return '';
    }
    return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
  }

  formatDateTime(dateStr: string | null): string {
    if (!dateStr) {
      return '';
    }
    const date = new Date(dateStr);
    if (Number.isNaN(date.getTime())) {
      return '';
    }
    return date.toLocaleString('en-US', {
      month: 'short', day: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  }

  getInitials(author: string): string {
    if (!author) {
      return '';
    }
    const parts = author.split(/[\s@.]+/).filter(Boolean);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return author.slice(0, 2).toUpperCase();
  }

  getInitialsColor(initials: string): string {
    const colors = ['#E65100', '#1565C0', '#2E7D32', '#6A1B9A', '#AD1457', '#00695C'];
    const idx = (initials.charCodeAt(0) || 0) % colors.length;
    return colors[idx];
  }

  addComment(): void {
    if (!this.po || !this.newComment.trim()) {
      return;
    }
    this.financesService.addComment(this.po.id, this.newComment.trim())
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.newComment = '';
          this.alertsService.openAlerts('Comment added successfully.', AlertsType.success);
          this._refreshDetails();
        },
        error: (err) => this._handlePoActionError(err, 'adding comment'),
      });
  }

  attachFile(): void {
    // TODO: file attachment not yet implemented
  }

  resubmitForApproval(): void {
    if (!this.po) {
      return;
    }
    const ref = this.dialog.open(PoConfirmDialogComponent, {
      width: '460px',
      panelClass: 'poc-dialog-panel',
      data: {
        title: 'Submit form?',
        message: 'Please check the information, changes are not allowed until approval.',
        confirmLabel: 'Yes, send request',
      },
    });
    ref.afterClosed().pipe(take(1)).subscribe(confirmed => {
      if (!confirmed || !this.po) {
        return;
      }
      this.financesService.resubmitPurchaseOrder(this.po.id)
        .pipe(take(1))
        .subscribe({
          next: () => {
            this.alertsService.openAlerts('Purchase order resubmitted successfully.', AlertsType.success);
            this._refreshDetails();
          },
          error: (err) => this._handleResubmitError(err),
        });
    });
  }

  retrySettlement(): void {
    if (!this.po) {
      return;
    }
    const ref = this.dialog.open(PoConfirmDialogComponent, {
      width: '460px',
      panelClass: 'poc-dialog-panel',
      data: {
        title: 'Retry settlement?',
        message: 'This will retry the settlement process for this purchase order.',
        confirmLabel: 'Yes, retry settlement',
      },
    });
    ref.afterClosed().pipe(take(1)).subscribe(confirmed => {
      if (!confirmed || !this.po) {
        return;
      }
      this.financesService.retrySettlement(this.po.id)
        .pipe(take(1))
        .subscribe({
          next: () => {
            this.alertsService.openAlerts('Settlement registration retried successfully.', AlertsType.success);
            this._refreshDetails();
          },
          error: (err) => this._handlePoActionError(err, 'retrying settlement'),
        });
    });
  }

  retryTrato(): void {
    if (!this.po) {
      return;
    }
    const ref = this.dialog.open(PoConfirmDialogComponent, {
      width: '460px',
      panelClass: 'poc-dialog-panel',
      data: {
        title: 'Retry Trato?',
        message: 'This will retry the Trato contract creation for this purchase order.',
        confirmLabel: 'Yes, retry Trato',
      },
    });
    ref.afterClosed().pipe(take(1)).subscribe(confirmed => {
      if (!confirmed || !this.po) {
        return;
      }
      this.financesService.retryTrato(this.po.id)
        .pipe(take(1))
        .subscribe({
          next: () => {
            this.alertsService.openAlerts('Trato contract creation retried successfully.', AlertsType.success);
            this._refreshDetails();
          },
          error: (err) => this._handlePoActionError(err, 'retrying Trato'),
        });
    });
  }

  approvePurchaseOrder(): void {
    if (!this.po) {
      return;
    }
    const ref = this.dialog.open(PoActionDialogComponent, {
      width: '576px',
      panelClass: 'pocd-dialog-panel',
      data: { type: PO_DIALOG_TYPE.APPROVE },
    });
    ref.afterClosed().pipe(take(1)).subscribe(result => {
      if (!result?.confirmed || !this.po) {
        return;
      }
      this.financesService.approvePurchaseOrder(this.po.id)
        .pipe(take(1))
        .subscribe({
          next: () => {
            this.alertsService.openAlerts('Purchase order approved successfully.', AlertsType.success);
            this._refreshDetails();
          },
          error: (err) => this._handlePoActionError(err, 'approving'),
        });
    });
  }

  returnToKam(): void {
    if (!this.po) {
      return;
    }
    const ref = this.dialog.open(PoActionDialogComponent, {
      width: '576px',
      panelClass: 'pocd-dialog-panel',
      data: { type: PO_DIALOG_TYPE.RETURN_TO_KAM },
    });
    ref.afterClosed().pipe(take(1)).subscribe(result => {
      if (!result?.confirmed || !this.po) {
        return;
      }
      this.financesService.returnToKam(this.po.id, result.reason || '')
        .pipe(take(1))
        .subscribe({
          next: () => {
            this.alertsService.openAlerts('Purchase order returned to KAM successfully.', AlertsType.success);
            this._refreshDetails();
          },
          error: (err) => this._handlePoActionError(err, 'returning to KAM'),
        });
    });
  }

  // Header action buttons (see Figma node 517:47206). Edit navigates to the PO detail form in
  // edit mode; Close (trash icon) closes/cancels the PO. Both are TODO until the close endpoint
  // lands in the contract — edit is wired now since the detail route already supports edit mode.
  editOrder(): void {
    if (!this.po) {
      return;
    }
    const country = this.route.snapshot.queryParamMap.get(PO_QUERY_PARAM.COUNTRY);
    const extras = country ? { queryParams: { [PO_QUERY_PARAM.COUNTRY]: country } } : undefined;
    this.router.navigate(['/financial-hub/purchase-orders', this.po.id, 'edit'], extras);
  }

  lockOrder(): void {
    if (!this.po) {
      return;
    }
    const ref = this.dialog.open(PoActionDialogComponent, {
      width: '576px',
      panelClass: 'pocd-dialog-panel',
      data: { type: PO_DIALOG_TYPE.LOCK },
    });
    ref.afterClosed().pipe(take(1)).subscribe(result => {
      if (!result?.confirmed || !this.po) {
        return;
      }
      this.financesService.closePurchaseOrder(this.po.id)
        .pipe(take(1))
        .subscribe({
          next: () => {
            this.alertsService.openAlerts('Purchase order closed successfully.', AlertsType.success);
            this._refreshDetails();
          },
          error: (err) => this._handlePoActionError(err, 'closing'),
        });
    });
  }

  unlockOrder(): void {
    if (!this.po) {
      return;
    }
    const ref = this.dialog.open(PoActionDialogComponent, {
      width: '576px',
      panelClass: 'pocd-dialog-panel',
      data: { type: PO_DIALOG_TYPE.UNLOCK },
    });
    ref.afterClosed().pipe(take(1)).subscribe(result => {
      if (!result?.confirmed || !this.po) {
        return;
      }
      this.financesService.reopenPurchaseOrder(this.po.id)
        .pipe(take(1))
        .subscribe({
          next: () => {
            this.alertsService.openAlerts('Purchase order reopened successfully.', AlertsType.success);
            this._refreshDetails();
          },
          error: (err) => this._handlePoActionError(err, 'reopening'),
        });
    });
  }

  // Resubmit: shows the backend `message` as-is (4xx); 5xx and empty responses use the generic text.
  private _handleResubmitError(err: any): void {
    const serverMessage = err?.error?.message ?? (typeof err?.error === 'string' ? err.error : null);
    const config = { ...this.alertsService.configAlerts, duration: PO_ERROR_ALERT_DURATION_MS };
    if (serverMessage && err?.status !== PO_HTTP_STATUS.INTERNAL_SERVER_ERROR) {
      this.alertsService.openAlerts(serverMessage, AlertsType.warning, config);
    } else if (err?.status === PO_HTTP_STATUS.NOT_FOUND) {
      this.alertsService.openAlerts('Purchase order not found.', AlertsType.warning, config);
    } else {
      this.alertsService.openAlerts('Error resubmitting purchase order. Please try again.', AlertsType.error, config);
    }
  }

  private _handlePoActionError(err: any, action: string): void {
    const status = err?.status;
    const serverMessage = err?.error?.message ?? (typeof err?.error === 'string' ? err.error : null);
    if (serverMessage) {
      this.alertsService.openAlerts(serverMessage, AlertsType.warning);
    } else if (status === 404) {
      this.alertsService.openAlerts('Purchase order not found.', AlertsType.warning);
    } else {
      this.alertsService.openAlerts(
        `Error ${action} purchase order. Please try again.`,
        AlertsType.error,
      );
    }
  }

  closeOrder(): void {
    if (!this.po) {
      return;
    }

    const ref = this.dialog.open(PoActionDialogComponent, {
      width: '576px',
      panelClass: 'pocd-dialog-panel',
      data: { type: PO_DIALOG_TYPE.CANCEL },
    });

    ref.afterClosed().pipe(take(1)).subscribe(result => {
      if (!result?.confirmed || !this.po) {
        return;
      }

      this.financesService.cancelPurchaseOrder(this.po.id)
        .pipe(take(1))
        .subscribe({
          next: () => {
            this.alertsService.openAlerts(
              'Purchase order canceled successfully.',
              AlertsType.success,
            );
            this._refreshDetails();
          },
          error: (err) => this._handlePoActionError(err, 'canceling'),
        });
    });
  }

  goBack(): void {
    this.router.navigate(['/financial-hub/purchase-orders']);
  }

  private _refreshDetails(): void {
    if (!this.po) {
      return;
    }
    this.financesService.getPurchaseOrder(this.po.id)
      .pipe(take(1))
      .subscribe(res => {
        this.po = res.data;
        this.statusCode = this.po.stage ? PO_LABEL_TO_CODE[this.po.stage] ?? null : null;
        this.comments = this.po.comments.map(c => this.toViewComment(c));
      });
  }

  private toViewComment(c: PurchaseOrderComment): ViewComment {
    return {
      id: c.id,
      author: c.author,
      initials: this.getInitials(c.author),
      date: this.formatDateTime(c.created_at),
      text: c.content,
    };
  }

  // Names resolved from nested objects returned by the detail endpoint.
  get companyToBillName(): string {
    return this.po?.company_to_bill?.name ?? '';
  }

  get contactName(): string {
    const c = this.po?.contact;
    if (!c) {
      return '';
    }
    return c.email ? `${c.name}  ·  ${c.email}` : c.name;
  }

  get agencyName(): string {
    return this.po?.agency?.name ?? '';
  }

  get revenueRecognitionMethodLabel(): string {
    const method = this.po?.revenue_recognition_method;
    if (!method) {
      return '';
    }
    const options = this.remoteConfig?.agency_recognition_method_options;
    if (!options?.length) {
      return method;
    }
    return options.find(o => o.value === method)?.label ?? method;
  }

  get requestReasonLabel(): string {
    const reason = this.po?.request_reason;
    if (!reason) {
      return '';
    }
    const options = this.remoteConfig?.request_reason_options;
    if (!options?.length) {
      return reason;
    }
    return options.find(o => o.code === reason)?.name ?? reason;
  }

  // Whether the Business Unit row renders at all — gated by the CMS rollout flag, same as the
  // create/edit form (section-general-info.component.ts's isBusinessUnitVisible).
  get isBusinessUnitVisible(): boolean {
    return this.poBusinessUnitEnabled;
  }

  // Determines whether to show "Maker to execute" or "Brand Group to execute" in General information.
  // While the flag is off, always show Maker to execute regardless of any stray business_unit value.
  get isRetailerAds(): boolean {
    return this.poBusinessUnitEnabled && this.po?.business_unit === BUSINESS_UNIT.RETAILER_ADS;
  }

  get businessUnitLabel(): string {
    const bu = this.po?.business_unit as BusinessUnit | null | undefined;
    return bu ? BUSINESS_UNIT_LABEL[bu] ?? '' : '';
  }

  // Budget type only applies to Partnerships POs, and only while the CMS rollout flag is on — see
  // PO_TYPE in purchase-order.constants.ts and FinancesConfig.budget_type_validation_enabled.
  get isBudgetTypeVisible(): boolean {
    return this.budgetTypeValidationEnabled && this.poTypeCode === PO_TYPE.PARTNERSHIPS;
  }

  get budgetTypeLabel(): string {
    const bt = this.po?.budget_type as BudgetType | null | undefined;
    return bt ? BUDGET_TYPE_LABEL[bt] ?? '' : '';
  }

  // Gated by the CMS rollout flag, same as the create/edit form (section-budget-dates.component.ts's
  // isExpirationDateVisible) — while off, hidden even for a Trato PO with an already-persisted value.
  get isExpirationDateVisible(): boolean {
    return this.tratoExpirationDateEnabled && this.sourceTypeCode === SOURCE_TYPE.TRATO;
  }
}
